import React, { useMemo, useState } from 'react'
import { Button, IconButton, Tooltip } from '@mui/material'
import { ArrowLeft, ArrowRight, Trash2 } from 'react-feather'
import { PAYMENT_GATEWAY, PAYMENT_METHOD } from 'src/assets/SettingsConfig'
import { Gateway, PaymentGatewayPriority, SelectionArgs } from 'src/lib/types/paymentSettings'
import { IPaymentGateways } from 'src/lib/types/settings'
import { permissionType } from 'src/lib/utils/constants'
import { useLocale } from 'src/lib/hooks/useLocal'
import DropdownButton from 'src/components/common/form-elements/DropdownButton'

interface PaymentGatewaySelectorsProps {
  method: PAYMENT_METHOD
  availableGateways: PAYMENT_GATEWAY[]
  isComingSoon?: boolean
  primaryGateway: Gateway | undefined
  secondaryGateway: Gateway | undefined
  isDisabled: boolean
  hasAutoPGAccess: boolean
  shouldOnlyShowText: boolean
  normalizedAllowedGateways: Record<string, IPaymentGateways>
  handleGateWaySelect: (args: SelectionArgs) => Promise<void>
  onGatewaySelect: (args: SelectionArgs) => Promise<void>
  setIsRemovalConfirmOpen: (isOpen: boolean) => void
}

// Actually, let's keep the logic as it was in PaymentMethodRow for stability
const PaymentGatewaySelectors: React.FC<PaymentGatewaySelectorsProps> = ({
  method,
  availableGateways,
  isComingSoon,
  primaryGateway,
  secondaryGateway,
  isDisabled,
  hasAutoPGAccess,
  shouldOnlyShowText,
  normalizedAllowedGateways,
  handleGateWaySelect,
  onGatewaySelect,
  setIsRemovalConfirmOpen
}) => {
  const { t } = useLocale()

  // Tracks which of the two dropdowns (if any) is open, and whether it's mid-update. Only one
  // can be open at a time, so a single pair of state values is enough to drive both.
  const [openPriority, setOpenPriority] = useState<PaymentGatewayPriority | null>(null)
  const [isSelectionPending, setIsSelectionPending] = useState(false)

  // Reserves a fixed-width slot for the option icon/spinner so the loading state doesn't shift
  // row layout - opt-in only for this dropdown, other DropdownButton usages are unaffected.
  const iconSlotClassName = 'flex items-center justify-center w-4 ml-2 shrink-0'

  const handleDropdownSelect = async (priority: PaymentGatewayPriority, gateway: PAYMENT_GATEWAY) => {
    setOpenPriority(priority)
    setIsSelectionPending(true)
    await handleGateWaySelect({
      method,
      priority,
      isRemoval: false,
      gateway
    })
    setIsSelectionPending(false)
    setOpenPriority(null)
  }

  const primaryAllowedGateways = useMemo(() => {
    const pgs = availableGateways.filter(gateway => gateway !== secondaryGateway?.pg_name)
    if (!primaryGateway) {
      pgs.unshift('SELECT' as PAYMENT_GATEWAY)
    }
    return pgs
  }, [availableGateways, secondaryGateway, primaryGateway])

  const secondaryAllowedGateways = useMemo(() => {
    const pgs = availableGateways.filter(gateway => gateway !== primaryGateway?.pg_name)
    if (!secondaryGateway) {
      pgs.unshift('SELECT' as PAYMENT_GATEWAY)
    }
    return pgs
  }, [availableGateways, primaryGateway, secondaryGateway])

  const createOptionsInner = (gateways: (PAYMENT_GATEWAY | 'SELECT')[], gateway: Gateway | undefined) => {
    const options = gateways.map(pg => {
      if (pg === 'SELECT') {
        return { value: null, label: 'Select' }
      }
      const gatewayDetails = normalizedAllowedGateways[pg]
      return {
        value: gatewayDetails.type,
        label: gatewayDetails.label,
        permissionId: permissionType.MULTI_PG_SUPPORT
      }
    })

    if (!gateway && gateways.includes('SELECT')) {
      return { options, selectedIndex: 0 }
    }

    const selectedIndex = gateways.findIndex(pg => pg === gateway?.pg_name)
    return { options, selectedIndex }
  }

  const { options: primaryOptions, selectedIndex: selectedPrimaryIndex } = useMemo(
    () => createOptionsInner(primaryAllowedGateways, primaryGateway),
    [primaryAllowedGateways, primaryGateway, normalizedAllowedGateways]
  )

  const { options: secondaryOptions, selectedIndex: selectedSecondaryIndex } = useMemo(
    () => createOptionsInner(secondaryAllowedGateways, secondaryGateway),
    [secondaryAllowedGateways, secondaryGateway, normalizedAllowedGateways]
  )

  const shouldAllowSecondaryGateway = hasAutoPGAccess && primaryGateway && availableGateways.length > 1
  const shouldShowSetupText = availableGateways.length === 0

  const secondaryToolTipText = useMemo(() => {
    if (!hasAutoPGAccess) {
      return t('payment.setup_pg_to_use_description')
    }
    if (!primaryGateway) {
      return t('payment.enable_by_selecting_a_primary_pg')
    } else if (availableGateways.length === 1) {
      return t('payment.enable_by_setting_more_than_one_pg')
    }
    return undefined
  }, [primaryGateway, availableGateways, hasAutoPGAccess, t])

  if (shouldOnlyShowText) {
    const primaryGatewayDetails = primaryGateway
      ? `${normalizedAllowedGateways[primaryGateway.pg_name].label} PG`
      : 'NA'
    return <span className='p-3 pl-8 flex items-center text-coal-light'>{primaryGatewayDetails}</span>
  }

  if (isComingSoon) {
    return (
      <>
        <span className='p-3 flex items-center justify-center text-coal-light'>{t('payment.coming_soon')}</span>
        <span className='p-3 flex items-center justify-center text-coal-light'>{t('payment.coming_soon')}</span>
      </>
    )
  }

  if (shouldShowSetupText) {
    return (
      <>
        <span className='p-3'>{t('payment.setup_pg_to_use')}</span>
        <span className='my-auto p-3'>{t('payment.setup_pg_to_use')}</span>
      </>
    )
  }

  return (
    <>
      <span className='py-3 px-11 flex items-center justify-center'>
        <div className='w-[180px]'>
          <DropdownButton
            options={primaryOptions}
            variant='outlined'
            size='large'
            selectedOption={selectedPrimaryIndex}
            handleClick={(_, value) => {
              if (value) {
                handleDropdownSelect(PaymentGatewayPriority.PRIMARY, value as PAYMENT_GATEWAY)
              }
            }}
            disabled={isDisabled}
            disableSelectedOption={!hasAutoPGAccess}
            fullWidth
            isOpen={openPriority === PaymentGatewayPriority.PRIMARY}
            onOpenChange={open => setOpenPriority(open ? PaymentGatewayPriority.PRIMARY : null)}
            isSelectionLoading={isSelectionPending && openPriority === PaymentGatewayPriority.PRIMARY}
            iconSlotClassName={iconSlotClassName}
          />
        </div>
      </span>
      <span className='py-3 px-11 disabled flex justify-center items-center relative'>
        {secondaryGateway && hasAutoPGAccess && (
          <Tooltip title='Swap primary and alternate pgs'>
            <Button
              variant='outlined'
              sx={{
                position: 'absolute',
                left: -18,
                padding: 0,
                minWidth: 'fit-content'
              }}
              onClick={() => {
                onGatewaySelect({
                  method,
                  priority: PaymentGatewayPriority.SECONDARY,
                  isRemoval: false,
                  gateway: secondaryGateway.pg_name,
                  swapGateways: true
                })
              }}
            >
              <span className='flex justify-center items-center relative w-8 h-8'>
                <ArrowLeft className='relative top-1 -right-1' size={16} />
                <ArrowRight className='relative -top-1 -left-1' size={16} />
              </span>
            </Button>
          </Tooltip>
        )}
        <div className='relative flex items-center justify-center w-[180px]'>
          <DropdownButton
            options={secondaryOptions}
            variant='outlined'
            size='large'
            selectedOption={selectedSecondaryIndex}
            isOpen={openPriority === PaymentGatewayPriority.SECONDARY}
            onOpenChange={open => setOpenPriority(open ? PaymentGatewayPriority.SECONDARY : null)}
            isSelectionLoading={isSelectionPending && openPriority === PaymentGatewayPriority.SECONDARY}
            iconSlotClassName={iconSlotClassName}
            handleClick={(_, value) => {
              if (value) {
                handleDropdownSelect(PaymentGatewayPriority.SECONDARY, value as PAYMENT_GATEWAY)
              }
            }}
            disabled={!shouldAllowSecondaryGateway || isDisabled}
            tooltipTitle={secondaryToolTipText}
            fullWidth
          />
        </div>
        {secondaryGateway && (
          <div className='absolute right-2 top-1/2 -translate-y-1/2'>
            <Tooltip title='Remove alternate payment gateway'>
              <IconButton size='small' onClick={() => setIsRemovalConfirmOpen(true)} disabled={isDisabled}>
                <Trash2 size={16} className='text-zinc-500' />
              </IconButton>
            </Tooltip>
          </div>
        )}
      </span>
    </>
  )
}

export default PaymentGatewaySelectors
